import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Button,
  Snackbar,
  Alert,
  Box,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';

export default function Referral() {
  const [form, setForm] = useState({ referrerName: '', referrerEmail: '', candidateName: '', candidateEmail: '', job: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.referrerName || !form.referrerEmail || !form.candidateName || !form.candidateEmail) {
      setSnack({ open: true, severity: 'error', message: 'Please fill required fields.' });
      return;
    }
    setSubmitting(true);
    try {
      // Placeholder for backend integration
      await new Promise(r => setTimeout(r, 600));
      setSnack({ open: true, severity: 'success', message: 'Referral submitted — thank you! We will follow up.' });
      setForm({ referrerName: '', referrerEmail: '', candidateName: '', candidateEmail: '', job: '', message: '' });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: 'Failed to submit referral.' });
    } finally {
      setSubmitting(false);
    }
  }

  function generateMessage() {
    // Lightweight client-side "AI" template generator. Replace with real OpenAI call if desired.
    const template = `Hi ${form.candidateName || 'there'},\n\nI wanted to refer you for the ${form.job || 'open role'} at NehaInfoTech. ${form.referrerName ? `I worked with ${form.candidateName} and can vouch for their experience in relevant technologies.` : ''}\n\nKey strengths: ${form.job ? form.job + ', ' : ''}strong communication, team player.\n\nRegards,\n${form.referrerName || ''}`;
    setForm(f => ({ ...f, message: template }));
    setSnack({ open: true, severity: 'info', message: 'Generated a suggested message — edit as needed.' });
  }

  return (
    <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} sx={{ minHeight: '80vh', py: 8,
      backgroundImage: "url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1650&q=80')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative'
    }}>
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(6,21,55,0.36), rgba(14,51,94,0.2))', zIndex: 0 }} />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, boxShadow: 12, overflow: 'hidden', backdropFilter: 'blur(6px)', backgroundColor: 'rgba(255,255,255,0.78)' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>Refer a Friend</Typography>
                <Typography variant="body1" color="text.secondary">Help us find great people — refer a candidate and we'll reach out if there's a fit. You'll be credited if they join.</Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick tips</Typography>
                <ul>
                  <li><Typography variant="body2">Include the candidate's resume or LinkedIn in the message.</Typography></li>
                  <li><Typography variant="body2">Mention relevant skills and experience.</Typography></li>
                </ul>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Your name" name="referrerName" value={form.referrerName} onChange={handleChange} required fullWidth />
                  <TextField label="Your email" name="referrerEmail" value={form.referrerEmail} onChange={handleChange} type="email" required fullWidth />

                  <TextField label="Candidate name" name="candidateName" value={form.candidateName} onChange={handleChange} required fullWidth />
                  <TextField label="Candidate email" name="candidateEmail" value={form.candidateEmail} onChange={handleChange} type="email" required fullWidth />
                  <TextField label="Role (optional)" name="job" value={form.job} onChange={handleChange} fullWidth />
                  <TextField label="Message (optional)" name="message" value={form.message} onChange={handleChange} multiline rows={4} fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="Generate message" onClick={generateMessage} disabled={submitting}>
                            <SmartToyIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button onClick={() => setForm({ referrerName: '', referrerEmail: '', candidateName: '', candidateEmail: '', job: '', message: '' })} disabled={submitting}>Reset</Button>
                    <Button type="submit" variant="contained" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Referral'}</Button>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
